import { NetworkStatus } from "users/networking/connections/network-status";

/**
 * Everything a marketplace result card can offer in its footer or its ⋮ menu.
 *
 * Before #5410 an individual card drew up to six outlined buttons of equal weight in a column, and
 * nothing told the viewer which one mattered. The card now leads with one contained button, offers
 * one outlined button beside it, and puts the rest in a menu.
 */
export enum MarketplaceResultAction {
  AcceptInvitation = "accept-invitation",
  AddToNetwork = "add-to-network",
  CancelInvitation = "cancel-invitation",
  CopyLink = "copy-link",
  CreateProposal = "create-proposal",
  RejectInvitation = "reject-invitation",
  RemoveFromNetwork = "remove-from-network",
  SendMessage = "send-message",
  SendToContact = "send-to-contact",
}

export const MARKETPLACE_RESULT_ACTION_LABELS: Record<MarketplaceResultAction, string> = {
  [MarketplaceResultAction.AcceptInvitation]: "Accept invitation",
  [MarketplaceResultAction.AddToNetwork]: "Add to network",
  [MarketplaceResultAction.CancelInvitation]: "Cancel invitation",
  // It copies the profile's address to the clipboard, which is all "Share" ever did. Send to a
  // contact is the other half of sharing (#2412).
  [MarketplaceResultAction.CopyLink]: "Copy link",
  [MarketplaceResultAction.CreateProposal]: "Create proposal",
  [MarketplaceResultAction.RejectInvitation]: "Reject invitation",
  [MarketplaceResultAction.RemoveFromNetwork]: "Remove from network",
  [MarketplaceResultAction.SendMessage]: "Send message",
  [MarketplaceResultAction.SendToContact]: "Send to a contact",
};

export type MarketplaceResultActionsInput = {
  /** Another firm. Teams: whenever proposals are allowed. Companies never. */
  canCreateProposal: boolean;
  /** The card has someone to message once connected. */
  canMessage: boolean;
  /** Where the viewer stands with the result's person. Absent when there is no one, or no network. */
  network?: NetworkStatus;
  /** The viewer may use the network, so can send the result to one of their contacts. */
  canSendToContact?: boolean;
};

export type MarketplaceResultActionPlan = {
  /** Contained, on the right of the footer. */
  primary?: MarketplaceResultAction;
  /** Outlined, beside the primary. */
  secondary?: MarketplaceResultAction;
  /** In the ⋮ menu, in this order. */
  menu: MarketplaceResultAction[];
  /** Quiet text on the left of the footer. */
  connectionStatus?: string;
};

const CONNECTION_STATUS_TEXT: Partial<Record<NetworkStatus, string>> = {
  [NetworkStatus.InNetwork]: "In your network",
  [NetworkStatus.Received]: "Invitation received",
  [NetworkStatus.Rejected]: "Invitation rejected",
  [NetworkStatus.Sent]: "Invitation sent",
};

/**
 * Decides which of a result's actions leads, which sits beside it, and which go in the menu. The
 * rules are the "Which button leads" board agreed on #5401:
 *
 * - The primary is the first available of Create proposal, Send message (only once connected), and
 *   the network action that moves a connection forward (Add to network, Accept invitation). Create
 *   proposal leads wherever it applies: it opens a draft with the result as its vendor leader, and a
 *   client who wants several firms to bid sends that draft to them. That is why the card has no
 *   Request proposal any more (#6464) -- the draft and its send-to-several-firms cover it.
 * - The secondary is Send message when connected and not already primary; otherwise the network
 *   action. Cancel invitation never leads — it undoes rather than does — but the board puts it in the
 *   footer as the secondary, since it is the one thing an unconnected viewer can do about the
 *   connection.
 * - The menu holds the two ways to share the result, Copy link and Send to a contact, then whatever
 *   would end the connection: Reject invitation, Remove from network. Send to a contact is about the
 *   viewer's network, not the result's, so it is offered for a result with nobody to connect with.
 *
 * Send message is never drawn disabled. Before #5410 it was, with the reason only in a tooltip; it
 * is now offered only when it works.
 */
export function planMarketplaceResultActions(input: MarketplaceResultActionsInput): MarketplaceResultActionPlan {
  const { canCreateProposal, canMessage, network, canSendToContact } = input;
  const connected = network === NetworkStatus.InNetwork;
  const message = canMessage && connected ? MarketplaceResultAction.SendMessage : undefined;
  const networkAction = getForwardNetworkAction(network);

  const leaders = [
    canCreateProposal ? MarketplaceResultAction.CreateProposal : undefined,
    message,
    networkAction === MarketplaceResultAction.CancelInvitation ? undefined : networkAction,
  ];
  const primary = leaders.find((action) => action !== undefined);
  const secondary = [message, networkAction].find((action) => action !== undefined && action !== primary);
  const placed = new Set([primary, secondary]);

  const menu: MarketplaceResultAction[] = [MarketplaceResultAction.CopyLink];
  if (canSendToContact) menu.push(MarketplaceResultAction.SendToContact);
  if (networkAction && !placed.has(networkAction)) menu.push(networkAction);
  if (network === NetworkStatus.Received) menu.push(MarketplaceResultAction.RejectInvitation);
  if (connected) menu.push(MarketplaceResultAction.RemoveFromNetwork);

  return {
    primary,
    secondary,
    menu,
    connectionStatus: network ? CONNECTION_STATUS_TEXT[network] : undefined,
  };
}

function getForwardNetworkAction(network: NetworkStatus | undefined): MarketplaceResultAction | undefined {
  switch (network) {
    case NetworkStatus.None:
      return MarketplaceResultAction.AddToNetwork;
    case NetworkStatus.Received:
      return MarketplaceResultAction.AcceptInvitation;
    case NetworkStatus.Sent:
      return MarketplaceResultAction.CancelInvitation;
    default:
      return undefined;
  }
}
